import { useState, useCallback } from "react";

export interface ModalState {
  isOpen: boolean;
  title?: string;
  message: string;
  type?: "loading" | "success" | "error" | "info" | "warning";
  showCloseButton?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
  onContinue?: () => void;
  onClose?: () => void; // Callback called when modal closes (via close button, auto-close, or backdrop)
  confirmText?: string;
  cancelText?: string;
  showButtons?: boolean;
  autoClose?: boolean;
  autoCloseDelay?: number;
  redirectUrl?: string;
  redirectDelay?: number;
  jsonData?: any;
  showJsonData?: boolean;
}

export const useModal = () => {
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    message: "",
  });

  const closeModal = useCallback(() => {
    setModalState((prev) => {
      // Call onClose callback if it exists
      if (prev.onClose) {
        prev.onClose();
      }
      // Reset to minimal state when closing
      return { 
        isOpen: false,
        message: "",
      };
    });
  }, []);

  const showModal = useCallback((config: Omit<ModalState, "isOpen">) => {
    // Explicitly set all properties to ensure nothing is undefined from previous state
    setModalState({
      isOpen: true,
      message: config.message,
      title: config.title,
      type: config.type,
      showCloseButton: config.showCloseButton,
      onConfirm: config.onConfirm,
      onCancel: config.onCancel,
      onContinue: config.onContinue,
      onClose: config.onClose,
      confirmText: config.confirmText,
      cancelText: config.cancelText,
      showButtons: config.showButtons,
      autoClose: config.autoClose,
      autoCloseDelay: config.autoCloseDelay,
      redirectUrl: config.redirectUrl,
      redirectDelay: config.redirectDelay,
      jsonData: config.jsonData,
      showJsonData: config.showJsonData,
    });
  }, []);

  // Convenience methods for common modal types
  const showLoading = useCallback((message: string, title?: string) => {
    showModal({
      message,
      title,
      type: "loading",
      showCloseButton: false,
      showButtons: false,
    });
  }, [showModal]);

  const showSuccess = useCallback((
    message: string, 
    title?: string, 
    options?: {
      autoClose?: boolean;
      autoCloseDelay?: number;
      redirectUrl?: string;
      redirectDelay?: number;
      onConfirm?: () => void;
      onContinue?: () => void;
      onClose?: () => void; // Callback called when modal closes
      jsonData?: any;
      showJsonData?: boolean;
    }
  ) => {
    const autoCloseValue = options?.autoClose ?? true;
    const autoCloseDelayValue = options?.autoCloseDelay ?? 3000;
    
    showModal({
      message,
      title,
      type: "success",
      showCloseButton: true,
      showButtons: !!options?.onConfirm,
      autoClose: autoCloseValue,
      autoCloseDelay: autoCloseDelayValue,
      redirectUrl: options?.redirectUrl,
      redirectDelay: options?.redirectDelay ?? 2000,
      onConfirm: options?.onConfirm,
      onContinue: options?.onContinue,
      onClose: options?.onClose,
      confirmText: "OK",
      jsonData: options?.jsonData,
      showJsonData: options?.showJsonData ?? false,
    });
  }, [showModal]);

  const showError = useCallback((
    message: string, 
    title?: string, 
    options?: {
      onConfirm?: () => void;
      onCancel?: () => void;
      onClose?: () => void; // Callback called when modal closes
      confirmText?: string;
      cancelText?: string;
    }
  ) => {
    showModal({
      message,
      title,
      type: "error",
      showCloseButton: true,
      showButtons: !!(options?.onConfirm || options?.onCancel),
      onConfirm: options?.onConfirm,
      onCancel: options?.onCancel,
      onClose: options?.onClose,
      confirmText: options?.confirmText ?? "OK",
      cancelText: options?.cancelText ?? "Cancel",
    });
  }, [showModal]);

  const showWarning = useCallback((
    message: string, 
    title?: string, 
    options?: {
      onConfirm?: () => void;
      onCancel?: () => void;
      confirmText?: string;
      cancelText?: string;
    }
  ) => {
    showModal({
      message,
      title,
      type: "warning",
      showCloseButton: true,
      showButtons: !!(options?.onConfirm || options?.onCancel),
      onConfirm: options?.onConfirm,
      onCancel: options?.onCancel,
      confirmText: options?.confirmText ?? "OK",
      cancelText: options?.cancelText ?? "Cancel",
    });
  }, [showModal]);

  const showInfo = useCallback((
    message: string, 
    title?: string, 
    options?: {
      onConfirm?: () => void;
      autoClose?: boolean;
      autoCloseDelay?: number;
    }
  ) => {
    showModal({
      message,
      title,
      type: "info",
      showCloseButton: true,
      showButtons: !!options?.onConfirm,
      autoClose: options?.autoClose ?? false,
      autoCloseDelay: options?.autoCloseDelay ?? 3000,
      onConfirm: options?.onConfirm,
      confirmText: "OK",
    });
  }, [showModal]);

  const showConfirm = useCallback((
    message: string, 
    title?: string, 
    options?: {
      onConfirm: () => void;
      onCancel?: () => void;
      confirmText?: string;
      cancelText?: string;
    }
  ) => {
    // Always provide onCancel - default to closing modal if not specified
    const handleCancel = options?.onCancel || closeModal;
    
    showModal({
      message,
      title,
      type: "warning",
      showCloseButton: true,
      showButtons: true,
      onConfirm: options?.onConfirm,
      onCancel: handleCancel,
      onClose: handleCancel, // Also close on backdrop/close button
      confirmText: options?.confirmText ?? "Confirm",
      cancelText: options?.cancelText ?? "Cancel",
    });
  }, [showModal, closeModal]);

  return {
    modalState,
    closeModal,
    showModal,
    showLoading,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showConfirm,
  };
};
